import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Tooltip from 'shared/components/Tooltip';
import Hint from 'shared/components/Hint';
import getScoreColor from 'shared/lib/getScoreColor';
import styles from './Dims.module.scss';

const SCORE_DIM_KEYS = ['capabilities', 'overview', 'trajectory', 'context', 'domain'];

// Per-dimension score breakdown (5 progress bars). App-specific i18n (label
// namespaces, gender context, shortened labels) stays with the caller via the
// getLabel/getTooltip callbacks; this component owns the dim list, colors and
// markup. `showValue` renders the % next to the label and switches to the
// larger visual (profile/job detail); without it the compact list-row visual
// is used. `stacked` stacks the bars vertically instead of one row of five.
// `tooltipOnIcon` moves the tooltip from the whole element to a Hint (i) icon
// next to the label.
const ScoreDims = ({ dims, getLabel, getTooltip, showValue, stacked, tooltipOnIcon, className }) => (
  <div className={cn(styles.dimensions, { [styles.stacked]: stacked }, className)}>
    {SCORE_DIM_KEYS.map((key) => {
      const dimScore = Math.round(dims[key] || 0);
      const color = getScoreColor(dimScore);
      const content = (
        <>
          <div className={cn(styles.dimensionHeader, { [styles.withValue]: showValue })}>
            <span className={styles.dimensionLabel}>
              {getLabel(key)}
              {tooltipOnIcon && (
                <Hint
                  iconClassName="la la-info-circle"
                  className={styles.dimensionHint}
                  tooltip={getTooltip(key, dimScore)}
                />
              )}
            </span>
            {showValue && <span style={{ color }}>{dimScore}%</span>}
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${dimScore}%`, background: color }} />
          </div>
        </>
      );
      return tooltipOnIcon ? (
        <div key={key} className={cn(styles.dimension, styles.withHint)}>{content}</div>
      ) : (
        <Tooltip key={key} content={getTooltip(key, dimScore)} spanClassName={styles.dimension}>
          {content}
        </Tooltip>
      );
    })}
  </div>
);

ScoreDims.propTypes = {
  dims: PropTypes.object.isRequired,
  getLabel: PropTypes.func.isRequired,
  getTooltip: PropTypes.func.isRequired,
  showValue: PropTypes.bool,
  stacked: PropTypes.bool,
  tooltipOnIcon: PropTypes.bool,
  className: PropTypes.string,
};

ScoreDims.defaultProps = {
  showValue: false,
  stacked: false,
  tooltipOnIcon: false,
  className: null,
};

export default ScoreDims;
